import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@shared/ui/dialog";
import { Button } from "@shared/ui/button";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { useMeterCycle, useUpdateReadCycle } from "../../hooks";
import { useParams } from "react-router-dom";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";

interface ReadCycleData {
  id: string;
  name: string;
  type: string;
  utilityService: string;
  routes: number;
  nextRun: string;
}

interface UpdateReadCycleModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedRoutes: string[];
  onMoveRoutes: (destinationCycleId: string) => void;
  estimatedMeters: number;
}

export const UpdateReadCycleModal: React.FC<UpdateReadCycleModalProps> = ({
  open,
  onOpenChange,
  selectedRoutes,
  onMoveRoutes,
  estimatedMeters,
}) => {
  const remoteUtilityId = useRemoteUtilityId();
  const { route_id } = useParams();
  const [selectedDestination, setSelectedDestination] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  const { data: meterCycleData } = useMeterCycle(route_id, searchTerm);
  const updateReadCycleMutation = useUpdateReadCycle();


  const readCycleData = meterCycleData?.results || [];


  // Handle search from GenericTable
  const handleSearch = (searchValue: string, paramName: string) => {
    setSearchTerm(searchValue);
  };

  const handleSelectReadCycle = (id: string) => {
    setSelectedDestination(id === selectedDestination ? null : id);
  };

  const handleMoveRoutes = async () => {
    if (!selectedDestination) return;

    const payload = {
      route_id: route_id,
      new_cycle_id: selectedDestination,
      remote_utility_id: remoteUtilityId,
    };

    try {
      await updateReadCycleMutation.mutateAsync(payload);
      onMoveRoutes(selectedDestination);
      onOpenChange(false);
    } catch (error) {

    }
  };

  const columns: TableColumn<ReadCycleData>[] = useMemo(
    () => [
      {
        key: "meterCycleName",
        header: "Read Cycle Name",
 
      },
      {
        key: "meterTypeDisplay",
        header: "Read Type",
  
      },
      {
        key: "productCode",
        header: "Utility Service",

      },
      {
      key: "routeCount",
      header: "Routes",
    },
    {
      key: "meters",
      header: "Meters",

      render: (_, row: any) => row.summary?.totalMeterCount ?? "NA",
    },
      {
        key: "consumerCount",
        header: "Consumers",

      },
      {
        key: "runCount",
        header: "Run Count",

      },
      {
        key: "nextRunDate",
        header: "Next Run Date",
  
      },
      {
        key: "action",
        header: "Action",
        width: "w-24",
  
        render: (_, row) => (
          <Button
            variant={selectedDestination === row.id ? "default" : "outline"}
            size="sm"
            onClick={() => handleSelectReadCycle(row.id)}
          >
            {selectedDestination === row.id ? "Selected" : "Select"}
          </Button>
        ),
      },
    ],
    [selectedDestination]
  );

  useEffect(() => {
    if (!open) {
      setSelectedDestination(null);
      setSearchTerm("");
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader className="flex items-start justify-between">
          <div>
            <DialogTitle className="text-xl">Update Read Cycle</DialogTitle>
            <DialogDescription>
              Select a read cycle to update this route. This update will be
              applied for upcoming runs of the selected read cycle.
            </DialogDescription>
          </div>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <GenericTable
            data={readCycleData}
            columns={columns}
            rowKey="id"
            searchPlaceholder="Search by cycle name or type..."
            searchParamName="search_data"
            searchDebounceMs={300}
            onSearch={handleSearch}
            emptyMessage="No read cycles found"
          />
        </div>
        <DialogFooter className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleMoveRoutes}
            disabled={!selectedDestination}
            className="bg-blue-600 hover:bg-blue-700"
          >
            Update Read Cycle
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};